import moment from 'moment'

/** Parse profile / API notification mark strings to unix ms. */
export function parseNotificationMarkToMs(mark: unknown): number | null {
  if (mark == null || mark === '') return null
  if (typeof mark === 'number') {
    const n = mark

    return n < 1e12 ? Math.floor(n * 1000) : Math.floor(n)
  }
  const s = String(mark).trim()
  if (s === '') return null
  const asNum = Number(s)
  if (!Number.isNaN(asNum) && /^-?\d+(\.\d+)?$/.test(s)) {
    return asNum < 1e12 ? Math.floor(asNum * 1000) : Math.floor(asNum)
  }
  const parsed = moment(s)

  return parsed.isValid() ? parsed.valueOf() : null
}

/** Keys we persist / reload when GET profile omits them (until API returns them). */
export const PROFILE_NOTIFICATION_MARK_KEYS = [
  'event_mark',
  'chat_notif_mark',
  'payment_notif_mark',
  'promise_notif_mark',
  'manager_notif_marks'
] as const

const SESSION_PREFIX = 'blytzpay_notification_marks:'

export function sessionKeyForNotificationMarks(
  profileId: string,
  merchantId: string
) {
  return `${SESSION_PREFIX}${profileId}:${merchantId}`
}

export function persistProfileNotificationMarksToSession(
  profileId: string,
  merchantId: string,
  profile: Record<string, unknown>
): void {
  try {
    const slice: Record<string, unknown> = {}
    for (const k of PROFILE_NOTIFICATION_MARK_KEYS) {
      if (k === 'manager_notif_marks') continue
      if (profile[k] !== undefined) slice[k] = profile[k]
    }
    const mgrMap = getManagerNotifMarkMap(profile)
    if (mgrMap != null) slice.manager_notif_marks = mgrMap
    const encoded = JSON.stringify(slice)
    sessionStorage.setItem(
      sessionKeyForNotificationMarks(profileId, merchantId),
      encoded
    )
  } catch {
    /* private mode / quota */
  }
}

export function readStoredProfileNotificationMarks(
  profileId: string,
  merchantId: string
): Record<string, unknown> | null {
  try {
    const raw = sessionStorage.getItem(
      sessionKeyForNotificationMarks(profileId, merchantId)
    )
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown

    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null
  } catch {
    return null
  }
}

function isEmptyScalarMark(v: unknown): boolean {
  return v == null || v === ''
}

function storedScalarIsNewerThanProfile(
  profileVal: unknown,
  storedVal: unknown
): boolean {
  if (isEmptyScalarMark(storedVal)) return false
  if (isEmptyScalarMark(profileVal)) return true
  const p = parseNotificationMarkToMs(profileVal)
  const s = parseNotificationMarkToMs(storedVal)
  if (p == null || s == null) return false

  return s > p
}

function isEmptyManagerMark(v: unknown): boolean {
  if (v == null) return true
  if (typeof v !== 'object' || Array.isArray(v)) return true

  return Object.keys(v).length === 0
}

/**
 * Coerce API shapes into `{ [accountManagerPkid]: notificationMarkFields }`.
 * Supports a dict on profile or a list of rows with a pkid field.
 * Each value holds timestamps like `chat_notif_mark`, `all`, `event_mark`, etc.
 */
export function coerceManagerNotifMarkMap(
  raw: unknown
): Record<string, unknown> | null {
  if (raw == null) return null
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>

    return Object.keys(o).length ? o : null
  }
  if (!Array.isArray(raw) || raw.length === 0) return null
  /** Drop manager identity / wrapper keys; keep only notification mark fields. */
  const notificationMarkFieldsFromRow = (
    row: Record<string, unknown>
  ): Record<string, unknown> => {
    const identityKeys = new Set([
      'pkid',
      'account_manager_pkid',
      'account_manager_id',
      'account_manager',
      'manager_id',
      'id',
      'marks',
      'mark'
    ])
    const markFields: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(row)) {
      if (!identityKeys.has(k)) markFields[k] = v
    }

    return markFields
  }
  const marksByManagerPkid: Record<string, unknown> = {}
  for (const item of raw) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue
    const o = item as Record<string, unknown>
    const pk =
      o.pkid ??
      o.account_manager_pkid ??
      o.account_manager_id ??
      o.manager_id ??
      o.account_manager ??
      o.id
    if (pk === undefined || pk === null || pk === '') continue
    const key = String(pk).trim()
    if (!key) continue
    if (
      o.marks != null &&
      typeof o.marks === 'object' &&
      !Array.isArray(o.marks)
    ) {
      marksByManagerPkid[key] = o.marks as Record<string, unknown>
    } else if (
      o.mark != null &&
      typeof o.mark === 'object' &&
      !Array.isArray(o.mark)
    ) {
      marksByManagerPkid[key] = o.mark as Record<string, unknown>
    } else {
      const markFields = notificationMarkFieldsFromRow(o)
      marksByManagerPkid[key] =
        Object.keys(markFields).length > 0 ? markFields : o
    }
  }

  return Object.keys(marksByManagerPkid).length ? marksByManagerPkid : null
}

/** Read `profile.manager_notif_marks` (object or coercible array from API). */
export function getManagerNotifMarkMap(
  profile: Record<string, unknown> | null | undefined
): Record<string, Record<string, unknown>> | null {
  if (!profile) return null
  if (!Object.prototype.hasOwnProperty.call(profile, 'manager_notif_marks')) {
    return null
  }
  const coerced = coerceManagerNotifMarkMap(profile.manager_notif_marks)

  return coerced != null
    ? (coerced as Record<string, Record<string, unknown>>)
    : null
}

/** Resolve `mgr[pkid]` when the server uses string vs numeric keys. */
export function resolveManagerNotifMarkEntry(
  mgr: Record<string, unknown> | null | undefined,
  pkid: number | string
): unknown {
  if (!mgr || pkid === '' || pkid === null || pkid === undefined) {
    return undefined
  }
  const s = String(pkid).trim()
  const n = Number(s)
  if (Object.prototype.hasOwnProperty.call(mgr, s)) return mgr[s]
  if (
    !Number.isNaN(n) &&
    Object.prototype.hasOwnProperty.call(mgr, String(n))
  ) {
    return mgr[String(n)]
  }
  for (const k of Object.keys(mgr)) {
    if (String(k) === s) return mgr[k]
    if (!Number.isNaN(n) && !Number.isNaN(Number(k)) && Number(k) === n) {
      return mgr[k]
    }
  }

  return undefined
}

/**
 * Build a patch to merge session-stored marks into profile when the API left them out.
 */
export function buildNotificationMarksReloadPatch(
  profile: Record<string, unknown>,
  stored: Record<string, unknown> | null
): Record<string, unknown> | null {
  if (!stored || typeof stored !== 'object') return null
  const patch: Record<string, unknown> = {}

  const scalarKeys = [
    'event_mark',
    'chat_notif_mark',
    'payment_notif_mark',
    'promise_notif_mark'
  ] as const
  for (const k of scalarKeys) {
    if (storedScalarIsNewerThanProfile(profile[k], stored[k])) {
      patch[k] = stored[k]
    }
  }

  const pm = getManagerNotifMarkMap(profile)
  /** Legacy session key `manager_notif_mark` (pre-rename). */
  const sm = coerceManagerNotifMarkMap(
    stored.manager_notif_marks ?? stored.manager_notif_mark
  )
  if (sm != null) {
    if (isEmptyManagerMark(pm)) {
      patch.manager_notif_marks = sm
    } else {
      patch.manager_notif_marks = mergeManagerNotifMarks(
        pm as Record<string, unknown>,
        sm as Record<string, unknown>
      )
    }
  }

  return Object.keys(patch).length ? patch : null
}

/** Django's `clear_events` response names each mark by scope, not by its profile field. */
const CLEAR_EVENTS_RESPONSE_FIELDS = {
  all: 'event_mark',
  chat: 'chat_notif_mark',
  payment: 'payment_notif_mark',
  promise: 'promise_notif_mark'
} as const satisfies Record<
  string,
  (typeof PROFILE_NOTIFICATION_MARK_KEYS)[number]
>

/**
 * The profile patch a `POST /api/clear-events/` response carries. Unset marks are left out, so they
 * never overwrite a mark the profile already holds.
 */
export function marksFromClearEventsResponse(
  data: unknown
): Record<string, unknown> {
  const marks: Record<string, unknown> = {}
  if (!data || typeof data !== 'object' || Array.isArray(data)) return marks
  const body = data as Record<string, unknown>
  for (const [from, to] of Object.entries(CLEAR_EVENTS_RESPONSE_FIELDS)) {
    if (!isEmptyScalarMark(body[from])) marks[to] = body[from]
  }
  const managerMarks = coerceManagerNotifMarkMap(body.account_manager_marks)
  if (managerMarks != null) marks.manager_notif_marks = managerMarks

  return marks
}

export function clearAllNotificationMarksSessions(): void {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i)
      if (k?.startsWith(SESSION_PREFIX)) sessionStorage.removeItem(k)
    }
  } catch {
    /* ignore */
  }
}

/**
 * Deep-merge manager notification marks (per pkid, per kind).
 * Incoming object keys override/append nested fields only.
 */
export function mergeManagerNotifMarks(
  existing: Record<string, unknown> | null | undefined,
  incoming: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  if (incoming === null) return null
  if (!incoming || typeof incoming !== 'object' || Array.isArray(incoming)) {
    return incoming as Record<string, unknown>
  }
  const base =
    existing && typeof existing === 'object' && !Array.isArray(existing)
      ? { ...existing }
      : {}
  for (const [pkid, val] of Object.entries(incoming)) {
    if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
      const prev = base[pkid]
      const prevObj =
        prev && typeof prev === 'object' && !Array.isArray(prev)
          ? (prev as Record<string, unknown>)
          : {}
      base[pkid] = { ...prevObj, ...(val as Record<string, unknown>) }
    } else {
      base[pkid] = val
    }
  }

  return base
}
